/**
 * Tradução das falhas de autenticação para linguagem do usuário.
 *
 * Vale para os dois casos: quando a API responde com erro conhecido e quando a
 * chamada nem chega a completar (rede, CORS, API hibernando). O segundo caso
 * não pode terminar em silêncio — foi o que deixou a tela de cadastro estática
 * em produção.
 */
export interface FalhaDeAutenticacao {
  code?: string;
  message?: string;
}

const POR_CODIGO: Record<string, string> = {
  EMAIL_NOT_VERIFIED:
    'Confirme seu e-mail antes de entrar. Procure a mensagem na sua caixa de entrada.',
  USER_ALREADY_EXISTS: 'Já existe uma conta com este e-mail.',
  /*
   * A negativa é a mesma para senha errada e para conta que só existe no
   * Google - o servidor responde igual nos dois casos, de propósito, para não
   * revelar quem tem conta aqui. Como não dá para distinguir, a mensagem carrega
   * as duas saídas: a pessoa reconhece a sua.
   */
  INVALID_EMAIL_OR_PASSWORD:
    'E-mail ou senha incorretos. Se você criou a conta com o Google, entre pelo botão acima.',
  PASSWORD_TOO_SHORT: 'A senha precisa ter pelo menos 8 caracteres.',
  PROVIDER_NOT_FOUND: 'A entrada com o Google não está disponível agora. Use seu e-mail e senha.',
};

export const MENSAGEM_DE_FALHA_INESPERADA =
  'Não conseguimos falar com o servidor. Verifique sua conexão e tente novamente.';

export function mensagemDeErro(
  erro: FalhaDeAutenticacao | null | undefined,
  padrao: string,
): string {
  if (!erro) return padrao;
  return (erro.code && POR_CODIGO[erro.code]) ?? padrao;
}

/**
 * As falhas que voltam **pelo endereço**, e não por uma resposta.
 *
 * O fluxo do Google sai do site e volta redirecionando; quando dá errado, a
 * API devolve a pessoa ao `/entrar` com `?error=<código>`. Sem esta tradução, o
 * visitante voltaria para uma tela de login idêntica à que deixou, sem nenhuma
 * pista do que aconteceu - o pior desfecho possível para quem só queria entrar.
 *
 * `access_denied` não é falha nossa nem dele: é o botão "cancelar" da tela do
 * Google. Devolve `null` para a tela não acusar erro nenhum de quem apenas
 * mudou de ideia.
 */
export function mensagemDoRetornoOAuth(codigo: string | null): string | null {
  if (!codigo) return null;
  if (codigo === 'access_denied') return null;

  const POR_RETORNO: Record<string, string> = {
    /*
     * O e-mail do Google já tem conta aqui, criada com senha e nunca
     * confirmada. Vincular assim mesmo deixaria quem cadastrou o endereço
     * primeiro - sem provar que é dele - herdar a conta de quem entra pelo
     * Google, então a recusa é proposital e o caminho é confirmar o e-mail.
     */
    /*
     * A frase não manda "entrar com senha para reenviar a confirmação", que
     * era a redação anterior: **o reenvio só existe no app do aluno**. Esta
     * mensagem é lida também no backoffice, onde não há esse botão, e uma
     * instrução que não se cumpre no lugar onde é lida é pior que instrução
     * nenhuma. O que está aqui é verdade nos dois.
     */
    account_not_linked:
      'Já existe uma conta com este e-mail, criada com senha e ainda não confirmada. Abra o link de confirmação que enviamos e depois use o Google.',
    email_not_found:
      'Sua conta do Google não devolveu um e-mail, e precisamos de um para criar o acesso.',
    signup_disabled: 'No momento não é possível criar conta pelo Google.',
  };

  return (
    POR_RETORNO[codigo] ??
    'Não conseguimos concluir a entrada com o Google. Tente de novo, ou use seu e-mail e senha.'
  );
}
